// ════════════════════════════════════════════════════════════════════════════
// COMUNICAÇÃO — Sessão 32 (30/09/2026 — Walber)
// Números do mês para o relatório da Secretaria de Saúde (Instagram).
// Tela só EXIBE o que o motor devolve (app/routers/comunicacao.py): nenhuma
// conta aqui, e o texto para a Coordenação também vem pronto do motor.
// Escritas (exigem login): editar/voltar um indicador e enviar a logo.
// "Exportar PDF" = impressão do navegador com a folha A4 .cm-folha, desenhada
// num portal (#cm-print-root, filho direto do <body>) FORA da árvore da tela.
// ════════════════════════════════════════════════════════════════════════════
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import {
  Box, Card, CardContent, Typography, Select, MenuItem, FormControl, InputLabel,
  Chip, Button, Alert, CircularProgress, Dialog, DialogTitle, DialogContent,
  DialogContentText, DialogActions, TextField, Snackbar, GlobalStyles,
} from '@mui/material';
import {
  Campaign, Home, HomeWork, LocalDrink, Place, WaterDrop, Science, Edit,
  ContentCopy, PictureAsPdf, WarningAmber, InfoOutlined, Upload, Undo,
} from '@mui/icons-material';
import { API_URL } from './config';
import { useAuth } from './AuthContext';
import ModuleHeader from './components/ModuleHeader';
import BotaoArquivar from './BotaoArquivar';
import { mesPorExtenso } from './arquivarCopia';
import '@fontsource/kristi';   // assinatura da folha (fonte guardada no próprio sistema)

// ─── Tipos (espelho do motor) ────────────────────────────────────────────────
interface Editado {
  motivo: string;
  editado_por: string | null;
  editado_em: string | null;
  valor_sistema_na_edicao: number | null;
}
interface Indicador {
  chave: string;
  ordem: number;
  rotulo: string;
  unidade: string;
  valor_sistema: number;
  valor_editado: number | null;
  valor_final: number;
  editado: Editado | null;
  detalhe: Record<string, number>;
}
interface Dados {
  mes: string;
  mes_nome: string;
  primeiro_dia: string;
  ultimo_dia: string;
  mes_encerrado: boolean;
  ultimo_boletim_normal: string | null;
  ultimo_boletim_pe: string | null;
  boletins_normais: number;
  boletins_pe: number;
  gerado_em: string;
  logo_disponivel: boolean;
  indicadores: Indicador[];
  texto_coordenacao: string;
}

// ─── Constantes visuais ──────────────────────────────────────────────────────
const MARINHO = '#0D3B66';
const TEAL = '#00707C';
const AZUL_EDITADO = '#3A6EA5';
const AMARELO_FUNDO = '#FFF3D4';
const AMARELO_BORDA = '#E9C66A';
const CINZA_TEXTO = '#4A5563';   // contraste ≈ 7,5:1 sobre branco
const LOGO_MAX_BYTES = 900 * 1024;
const MESES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho',
  'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

const ICONES: Record<string, React.ReactNode> = {
  imoveis_visitados: <Home />,
  imoveis_tratados: <HomeWork />,
  depositos_tratados: <LocalDrink />,
  pe_trabalhados: <Place />,
  larvicida_gramas: <WaterDrop />,
  amostras_examinadas: <Science />,
};

// ─── Auxiliares ──────────────────────────────────────────────────────────────
const fmt = (v: number, casas = 0) =>
  (v ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: casas });

const ddmm = (iso: string | null) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}` : '');

const hojeISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const mesAtual = () => hojeISO().slice(0, 7);

/** Meses de 2026-04 até o mês atual (mais recente primeiro). */
function listaMeses(): string[] {
  const out: string[] = [];
  let [a, m] = [2026, 4];
  const [aF, mF] = mesAtual().split('-').map(Number);
  while (a < aF || (a === aF && m <= mF)) {
    out.push(`${a}-${String(m).padStart(2, '0')}`);
    m += 1;
    if (m > 12) { m = 1; a += 1; }
  }
  return out.reverse();
}

const nomeMes = (mes: string) => `${MESES[Number(mes.slice(5, 7)) - 1]}/${mes.slice(0, 4)}`;

/** Dia de entrega = dia 30 do mês (ou o último dia, em fevereiro). */
function diaEntrega(ultimoDia: string): string {
  const ultimo = Number(ultimoDia.slice(8, 10));
  return `${ultimoDia.slice(0, 8)}${String(Math.min(30, ultimo)).padStart(2, '0')}`;
}

/** "1.855" -> 1855 ; "8.232,5" -> 8232.5 */
const converterValor = (txt: string) => Number(txt.trim().replace(/\./g, '').replace(',', '.'));

function mensagemErro(e: any, acao: string): string {
  const st = e?.response?.status;
  if (st === 401) return `Faça login novamente para ${acao}.`;
  const det = e?.response?.data?.detail;
  if (typeof det === 'string') return det;
  if (Array.isArray(det)) return det.map((d: any) => d?.msg).filter(Boolean).join(' · ') || 'Dados inválidos.';
  return 'Não foi possível falar com o sistema. Tente de novo.';
}

function valorTexto(i: Indicador): string {
  const casas = i.chave === 'larvicida_gramas' ? 1 : 0;
  return fmt(i.valor_final, casas) + (i.unidade ? ` ${i.unidade}` : '');
}

function linhaDetalhe(i: Indicador): string {
  const d = i.detalhe || {};
  switch (i.chave) {
    case 'imoveis_visitados':
      return `Imóveis trabalhados · normal ${fmt(d.normal)} + PE ${fmt(d.pe)}`;
    case 'imoveis_tratados':
      return `Focal + perifocal · normal ${fmt(d.normal)} + PE ${fmt(d.pe)}`;
    case 'depositos_tratados':
      return `Larvicida 1 + 2 · normal ${fmt(d.normal)} + PE ${fmt(d.pe)}`;
    case 'pe_trabalhados':
      return `${fmt(d.boletins_pe)} ${d.boletins_pe === 1 ? 'boletim' : 'boletins'} de Ponto Estratégico`;
    case 'larvicida_gramas': {
      const kg = (i.valor_final / 1000).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
      return `≈ ${kg} kg · normal ${fmt(d.normal, 1)} g + PE ${fmt(d.pe, 1)} g`;
    }
    case 'amostras_examinadas':
      return `Tubitos com resultado (data da análise) · LIRAa: ${fmt(d.liraa)}`
        + (d.rotina_pe > 0 ? ` · PE: ${fmt(d.rotina_pe)}` : '');
    default:
      return '';
  }
}

function quandoEditado(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth() + 1)} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

async function copiarTexto(texto: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(texto);
    return true;
  } catch {
    try {
      const ta = document.createElement('textarea');
      ta.value = texto;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  }
}

// ─── CSS: folha A4 só na impressão ───────────────────────────────────────────
// A folha mora em #cm-print-root (filho direto do <body>, via portal). Na
// impressão, tudo que é filho do <body> some, menos ele. Altura automática:
// nada de altura fixa (evita 2ª página em branco).
const ESTILO_FOLHA = `
  .cm-num { font-variant-numeric: tabular-nums; }
  #cm-print-root { display: none; }
  @media print {
    @page { size: A4 portrait; margin: 14mm; }
    html, body { background: #fff !important; }
    body > *:not(#cm-print-root) { display: none !important; }
    #cm-print-root { display: block !important; }
    #cm-print-root, #cm-print-root * { visibility: visible !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    #cm-print-root .cm-folha { display: block !important; position: static !important; width: 100% !important; box-shadow: none !important; margin: 0 !important; }
  }
  .cm-folha { font-family: Arial, Helvetica, sans-serif; color: #111; }
  .cm-folha .cm-topo { display: flex; align-items: center; gap: 16px; padding-bottom: 10px; border-bottom: 3px solid ${MARINHO}; }
  .cm-folha .cm-logo { max-width: 300px; max-height: 110px; width: auto; height: auto; object-fit: contain; }
  .cm-folha .cm-inst { font-size: 13px; line-height: 1.45; }
  .cm-folha .cm-inst b { font-size: 15px; }
  .cm-folha h1 { font-size: 34px; color: ${MARINHO}; margin: 26px 0 4px; font-weight: 800; }
  .cm-folha .cm-sub { font-size: 15px; margin: 0 0 6px; }
  .cm-folha .cm-ref { font-size: 16px; color: ${TEAL}; font-weight: 600; margin: 0 0 18px; }
  .cm-folha .cm-gerado { font-weight: 400; color: #333; }
  .cm-folha table { width: 100%; border-collapse: collapse; font-size: 15px; }
  .cm-folha th { background: ${MARINHO}; color: #fff; text-align: left; padding: 10px 12px; font-size: 13px; letter-spacing: .5px; }
  .cm-folha td { padding: 12px; border-bottom: 1px solid #d9dde3; }
  .cm-folha tbody tr:nth-child(even) td { background: #f2f4f7; }
  .cm-folha .cm-c { text-align: center; width: 48px; }
  .cm-folha .cm-d { text-align: right; font-weight: 700; font-variant-numeric: tabular-nums; white-space: nowrap; }
  .cm-folha .cm-assin { width: 320px; margin: 56px auto 0; text-align: center; }
  .cm-folha .cm-assin-nome { font-family: 'Kristi', cursive; font-style: normal; font-size: 30px; color: #1A3A8F;
    text-align: center; line-height: 1; margin-bottom: -4px; display: inline-block;
    transform: rotate(-3deg); transform-origin: center;
    -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .cm-folha .cm-assin-linha { border-top: 1px solid #111; width: 100%; }
  .cm-folha .cm-assin-cargo { font-size: 13px; font-weight: 600; text-align: center; padding-top: 6px; }
`;

// ════════════════════════════════════════════════════════════════════════════
// Cartão de um indicador (exibição + edição no próprio cartão)
// ════════════════════════════════════════════════════════════════════════════
function CartaoIndicador({ ind, mes, headers, onAtualizado, onAviso }: {
  ind: Indicador;
  mes: string;
  headers: Record<string, string> | undefined;
  onAtualizado: (d: Dados) => void;
  onAviso: (msg: string) => void;
}) {
  const [editando, setEditando] = useState(false);
  const [valorTxt, setValorTxt] = useState('');
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState('');
  const [enviando, setEnviando] = useState(false);

  const cor = ind.ordem <= 3 ? MARINHO : TEAL;
  const decimal = ind.chave === 'larvicida_gramas';

  const abrir = () => {
    setValorTxt(fmt(ind.valor_final, decimal ? 1 : 0));
    setMotivo('');
    setErro('');
    setEditando(true);
  };

  const salvar = async () => {
    const v = converterValor(valorTxt);
    if (!valorTxt.trim() || !Number.isFinite(v) || v < 0) { setErro('Digite um número válido (0 ou mais).'); return; }
    if (!decimal && !Number.isInteger(v)) { setErro('Este número deve ser inteiro (sem vírgula).'); return; }
    if (decimal && Math.abs(Math.round(v * 10) - v * 10) > 1e-6) { setErro('Use no máximo 1 casa decimal.'); return; }
    if (motivo.trim().length < 5) { setErro('Escreva o motivo da alteração (pelo menos 5 letras).'); return; }
    setEnviando(true); setErro('');
    try {
      const r = await axios.put<Dados>(`${API_URL}/api/comunicacao/${mes}/${ind.chave}`,
        { valor: v, motivo: motivo.trim() }, { headers });
      onAtualizado(r.data);
      setEditando(false);
      onAviso('Salvo');
    } catch (e) {
      setErro(mensagemErro(e, 'editar'));
    } finally {
      setEnviando(false);
    }
  };

  const voltarAoSistema = async () => {
    if (!window.confirm(`Voltar "${ind.rotulo}" ao número do sistema (${fmt(ind.valor_sistema, decimal ? 1 : 0)})?`)) return;
    setEnviando(true); setErro('');
    try {
      const r = await axios.delete<Dados>(`${API_URL}/api/comunicacao/${mes}/${ind.chave}`, { headers });
      onAtualizado(r.data);
      setEditando(false);
      onAviso('Voltou ao número do sistema');
    } catch (e) {
      setErro(mensagemErro(e, 'editar'));
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Card sx={{
      borderRadius: 3, position: 'relative', height: '100%',
      border: ind.editado ? `2px solid ${AZUL_EDITADO}` : '1px solid #e1e5ea',
      boxShadow: 'none',
    }}>
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
          <Box sx={{
            width: 48, height: 48, borderRadius: 2, bgcolor: cor, color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {ICONES[ind.chave]}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {ind.editado && (
              <Chip label="Editado" size="small" sx={{ bgcolor: AZUL_EDITADO, color: '#fff', fontWeight: 700 }} />
            )}
            <Typography sx={{ fontWeight: 800, color: CINZA_TEXTO, fontSize: 15 }}>{ind.ordem}</Typography>
          </Box>
        </Box>

        <Typography className="cm-num" sx={{ fontSize: 50, fontWeight: 800, lineHeight: 1.05, color: cor }}>
          {fmt(ind.valor_final, decimal ? 1 : 0)}
          {ind.unidade && <Box component="span" sx={{ fontSize: 24, fontWeight: 700, ml: 0.75 }}>{ind.unidade}</Box>}
        </Typography>
        <Typography sx={{ fontSize: 17, fontWeight: 600, mt: 0.75, color: '#1b2430' }}>{ind.rotulo}</Typography>
        <Typography className="cm-num" sx={{ fontSize: 14, color: CINZA_TEXTO, mt: 0.5 }}>{linhaDetalhe(ind)}</Typography>

        {ind.editado && (
          <Typography className="cm-num" sx={{ fontSize: 13.5, color: AZUL_EDITADO, mt: 1, lineHeight: 1.45 }}>
            Sistema: {fmt(ind.valor_sistema, decimal ? 1 : 0)}{ind.unidade ? ` ${ind.unidade}` : ''}
            {' · '}Motivo: {ind.editado.motivo}
            {ind.editado.editado_por ? ` · por ${ind.editado.editado_por}` : ''}
            {ind.editado.editado_em ? ` em ${quandoEditado(ind.editado.editado_em)}` : ''}
          </Typography>
        )}

        {!editando && (
          <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
            <Button variant="outlined" startIcon={<Edit />} onClick={abrir} sx={{ minHeight: 44 }}>Editar</Button>
            {ind.editado && (
              <Button variant="text" startIcon={<Undo />} onClick={voltarAoSistema} disabled={enviando}
                      sx={{ minHeight: 44, color: AZUL_EDITADO }}>
                Voltar ao do sistema
              </Button>
            )}
          </Box>
        )}

        {editando && (
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <TextField
              label={`Valor${ind.unidade ? ` (${ind.unidade})` : ''}`}
              value={valorTxt}
              onChange={e => setValorTxt(e.target.value)}
              slotProps={{ htmlInput: { inputMode: 'decimal' } }}
              size="small"
              fullWidth
            />
            <TextField
              label="Motivo (obrigatório)"
              placeholder="Ex.: incluir resultados que o laboratório entregou hoje"
              value={motivo}
              onChange={e => setMotivo(e.target.value)}
              size="small"
              multiline
              minRows={2}
              fullWidth
            />
            {erro && <Alert severity="error" sx={{ py: 0 }}>{erro}</Alert>}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button variant="contained" onClick={salvar} disabled={enviando} sx={{ minHeight: 44, bgcolor: cor }}>
                {enviando ? <CircularProgress size={20} color="inherit" /> : 'Salvar'}
              </Button>
              <Button variant="outlined" onClick={() => setEditando(false)} disabled={enviando} sx={{ minHeight: 44 }}>
                Cancelar
              </Button>
              {ind.editado && (
                <Button variant="text" startIcon={<Undo />} onClick={voltarAoSistema} disabled={enviando}
                        sx={{ minHeight: 44, color: AZUL_EDITADO }}>
                  Voltar ao do sistema
                </Button>
              )}
            </Box>
          </Box>
        )}
        {!editando && erro && <Alert severity="error" sx={{ mt: 1.5, py: 0 }}>{erro}</Alert>}
      </CardContent>
    </Card>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Folha A4 (só aparece na impressão)
// ════════════════════════════════════════════════════════════════════════════
function FolhaPDF({ dados, logoVer }: { dados: Dados; logoVer: number }) {
  const hoje = hojeISO();
  const geradoEm = `${hoje.slice(8, 10)}/${hoje.slice(5, 7)}/${hoje.slice(0, 4)}`;
  return (
    <div className="cm-folha">
      <div className="cm-topo">
        {dados.logo_disponivel && (
          <img className="cm-logo" src={`${API_URL}/api/comunicacao/logo?v=${logoVer}`} alt="Logo do município" />
        )}
        <div className="cm-inst">
          <b>PREFEITURA MUNICIPAL DE MILAGRES</b><br />
          Secretaria Municipal de Saúde<br />
          Coordenação de Vigilância em Saúde
        </div>
      </div>
      <h1>Vigilância Epidemiológica</h1>
      <p className="cm-sub">Controle de Endemias — Agentes de Combate às Endemias (ACE)</p>
      <p className="cm-ref">
        Referência: {dados.mes_nome}<span className="cm-gerado"> · Gerado em {geradoEm}</span>
      </p>
      <table>
        <thead>
          <tr><th className="cm-c">Nº</th><th>INDICADOR</th><th style={{ textAlign: 'right' }}>QUANTIDADE</th></tr>
        </thead>
        <tbody>
          {dados.indicadores.map(i => (
            <tr key={i.chave}>
              <td className="cm-c">{i.ordem}</td>
              <td>{i.rotulo}</td>
              <td className="cm-d">{valorTexto(i)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="cm-assin">
        <div className="cm-assin-nome">Walber Reis dos Santos</div>
        <div className="cm-assin-linha" />
        <div className="cm-assin-cargo">Supervisão dos Agentes de Combate às Endemias</div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
export default function Comunicacao({ onVoltar }: { onVoltar: () => void }) {
  const { token } = useAuth();
  const [mes, setMes] = useState(mesAtual());
  const [dados, setDados] = useState<Dados | null>(null);
  // Arquivos: dados do documento para o botão "Arquivar"
  const pedidoArquivo = useMemo(() => (dados && mes ? {
    pasta: 'comunicacao' as const, tipo: 'comunicacao', chave: `comunicacao|${mes}`,
    titulo: `Comunicação · ${mesPorExtenso(mes)}`, mes, referencia: mesPorExtenso(mes), avisos: [] as string[],
  } : null), [dados, mes]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const [aviso, setAviso] = useState('');
  const [logoVer, setLogoVer] = useState(Date.now());
  const [enviandoLogo, setEnviandoLogo] = useState(false);
  const [erroLogo, setErroLogo] = useState('');
  const [dlgSemLogo, setDlgSemLogo] = useState(false);
  const seq = useRef(0);
  const inputLogo = useRef<HTMLInputElement>(null);
  const [printRoot, setPrintRoot] = useState<HTMLElement | null>(null);

  // Portal da folha A4: <div id="cm-print-root"> filho direto do <body>,
  // criado ao abrir a tela e removido ao sair dela.
  useEffect(() => {
    let el = document.getElementById('cm-print-root');
    if (!el) {
      el = document.createElement('div');
      el.id = 'cm-print-root';
      document.body.appendChild(el);
    }
    setPrintRoot(el);
    return () => { el?.parentNode?.removeChild(el); };
  }, []);

  const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
  const meses = listaMeses();

  const carregar = useCallback(async () => {
    const meu = ++seq.current;
    setCarregando(true); setErro('');
    try {
      const r = await axios.get<Dados>(`${API_URL}/api/comunicacao/dados`, { params: { mes } });
      if (meu !== seq.current) return;
      setDados(r.data);
    } catch (e) {
      if (meu !== seq.current) return;
      setDados(null);
      setErro(mensagemErro(e, 'ver os dados'));
    } finally {
      if (meu === seq.current) setCarregando(false);
    }
  }, [mes]);

  useEffect(() => { carregar(); }, [carregar]);

  const copiar = async () => {
    if (!dados) return;
    setAviso((await copiarTexto(dados.texto_coordenacao)) ? 'Texto copiado' : 'Não foi possível copiar. Selecione o texto e copie à mão.');
  };

  const imprimir = () => {
    if (!dados) return;
    const antigo = document.title;
    const [nm, ano] = dados.mes_nome.split('/');
    const semAcento = nm.normalize('NFD').replace(/[̀-ͯ]/g, '');
    document.title = `Vigilancia_Epidemiologica_${semAcento}_${ano}`;
    const restaurar = () => { document.title = antigo; window.removeEventListener('afterprint', restaurar); };
    window.addEventListener('afterprint', restaurar);
    // Antes de imprimir: pré-carrega a logo (mesma URL da folha) e a fonte da
    // assinatura (a folha está escondida na tela, então o navegador não baixa
    // a fonte sozinho). Imprime UMA vez: quando tudo chegar, ou em 3 s.
    let feito = false;
    const disparar = () => { if (feito) return; feito = true; window.print(); };
    const logo = new Promise<void>(res => {
      if (!dados.logo_disponivel) { res(); return; }
      const img = new Image();
      img.onload = () => res();
      img.onerror = () => res();
      img.src = `${API_URL}/api/comunicacao/logo?v=${logoVer}`;
    });
    const fonte = document.fonts
      ? Promise.all([document.fonts.load("30px 'Kristi'"), document.fonts.ready]).then(() => undefined, () => undefined)
      : Promise.resolve();
    Promise.all([logo, fonte]).then(disparar, disparar);
    setTimeout(disparar, 3000);
  };

  const exportar = () => {
    if (!dados) return;
    if (!dados.logo_disponivel) { setDlgSemLogo(true); return; }
    imprimir();
  };

  const escolherLogo = () => inputLogo.current?.click();

  const enviarLogo = async (ev: React.ChangeEvent<HTMLInputElement>) => {
    const arq = ev.target.files?.[0];
    ev.target.value = '';
    if (!arq) return;
    setErroLogo('');
    if (arq.size > LOGO_MAX_BYTES) {
      setErroLogo('A logo precisa ter até 900 KB. Diminua a imagem e tente de novo.');
      return;
    }
    const fd = new FormData();
    fd.append('arquivo', arq);
    setEnviandoLogo(true);
    try {
      await axios.post(`${API_URL}/api/comunicacao/logo`, fd, { headers });
      setLogoVer(Date.now());
      setAviso('Logo atualizada');
      await carregar();
    } catch (e) {
      setErroLogo(mensagemErro(e, 'enviar a logo'));
    } finally {
      setEnviandoLogo(false);
    }
  };

  // Faixa de situação
  const faixa = () => {
    if (!dados) return null;
    const temNormal = !!dados.ultimo_boletim_normal;
    const temPE = !!dados.ultimo_boletim_pe;
    if (!temNormal && !temPE) {
      return <Alert severity="info" sx={{ mb: 2 }}>Nenhum boletim digitado neste mês.</Alert>;
    }
    const ultimo = temNormal ? ddmm(dados.ultimo_boletim_normal) : ddmm(dados.ultimo_boletim_pe);
    const pe = temNormal && temPE ? ` (PE: ${ddmm(dados.ultimo_boletim_pe)})` : '';
    if (dados.mes_encerrado) {
      return (
        <Alert severity="info" icon={<InfoOutlined />} sx={{ mb: 2 }}>
          Mês encerrado. Último boletim: {ultimo}{pe}.
        </Alert>
      );
    }
    const mesMin = MESES[Number(dados.mes.slice(5, 7)) - 1].toLowerCase();
    const entrega = diaEntrega(dados.ultimo_dia);
    const eHoje = entrega === hojeISO();
    return (
      <Box sx={{
        mb: 2, p: 1.75, borderRadius: 2, bgcolor: AMARELO_FUNDO, border: `1px solid ${AMARELO_BORDA}`,
        display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap',
      }}>
        <WarningAmber sx={{ color: '#8A5A00' }} />
        <Typography sx={{ flex: 1, minWidth: 220, fontSize: 15, color: '#3d2e00' }}>
          Último boletim digitado em {mesMin}: {ultimo}{pe}. Se ainda há boletins de {mesMin} para digitar,
          estes números vão subir quando forem lançados.
        </Typography>
        <Chip label={eHoje ? `Entrega: hoje, ${ddmm(entrega)}` : `Entrega: ${ddmm(entrega)}`}
              sx={{ fontWeight: 700, bgcolor: '#fff', border: `1px solid ${AMARELO_BORDA}` }} />
      </Box>
    );
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50' }}>
      <GlobalStyles styles={ESTILO_FOLHA} />

      <Box className="cm-tela">
        <ModuleHeader
          titulo="Comunicação"
          subtitulo="Números do mês para o relatório da Secretaria de Saúde (Instagram) · entrega à Coordenação todo dia 30"
          icone={<Campaign fontSize="inherit" />}
          accentColor={MARINHO}
          onVoltar={onVoltar}
        />

        <Box sx={{ px: { xs: 2, md: 4 }, pb: 6, maxWidth: 1400, mx: 'auto' }}>
          {/* Mês + ações */}
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end', mb: 2 }}>
            {carregando && <CircularProgress size={22} />}
            <FormControl size="small" sx={{ minWidth: 200, bgcolor: 'background.paper' }}>
              <InputLabel>Mês de referência</InputLabel>
              <Select label="Mês de referência" value={mes} onChange={e => setMes(String(e.target.value))}
                      sx={{ minHeight: 44 }}>
                {meses.map(m => <MenuItem key={m} value={m}>{nomeMes(m)}</MenuItem>)}
              </Select>
            </FormControl>
            <Button variant="outlined" startIcon={<ContentCopy />} onClick={copiar} disabled={!dados}
                    sx={{ minHeight: 44, borderColor: MARINHO, color: MARINHO }}>
              Copiar texto
            </Button>
            <Button variant="contained" startIcon={<PictureAsPdf />} onClick={exportar} disabled={!dados}
                    sx={{ minHeight: 44, bgcolor: MARINHO, '&:hover': { bgcolor: '#0a2e50' } }}>
              Exportar PDF
            </Button>
            <BotaoArquivar alvo={() => document.getElementById('cm-print-root')} pedido={pedidoArquivo} />
          </Box>

          {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}

          {faixa()}

          {/* 6 cartões */}
          {dados && (
            <Box sx={{
              display: 'grid', gap: 2, mb: 3,
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
            }}>
              {dados.indicadores.map(ind => (
                <CartaoIndicador key={ind.chave} ind={ind} mes={dados.mes} headers={headers}
                                 onAtualizado={setDados} onAviso={setAviso} />
              ))}
            </Box>
          )}

          {/* Linha de baixo: texto · logo · como funciona */}
          {dados && (
            <Box sx={{
              display: 'grid', gap: 2,
              gridTemplateColumns: { xs: '1fr', md: '1.4fr 1fr 1fr' },
            }}>
              <Card sx={{ borderRadius: 3, boxShadow: 'none', border: '1px solid #e1e5ea' }}>
                <CardContent>
                  <Typography sx={{ fontWeight: 800, fontSize: 17, color: MARINHO, mb: 1 }}>
                    Texto pronto para a Coordenação
                  </Typography>
                  <Box className="cm-num" sx={{
                    fontFamily: 'ui-monospace, Menlo, Consolas, monospace', whiteSpace: 'pre-line',
                    fontSize: 14, bgcolor: '#f5f7fa', border: '1px solid #e1e5ea', borderRadius: 2, p: 1.5,
                  }}>
                    {dados.texto_coordenacao}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1.5, flexWrap: 'wrap' }}>
                    <Button variant="outlined" startIcon={<ContentCopy />} onClick={copiar}
                            sx={{ minHeight: 44, borderColor: MARINHO, color: MARINHO }}>
                      Copiar
                    </Button>
                    <Typography sx={{ fontSize: 13.5, color: CINZA_TEXTO, flex: 1, minWidth: 180 }}>
                      Cola direto no WhatsApp da Coordenação. Sai com os valores editados, se houver.
                    </Typography>
                  </Box>
                </CardContent>
              </Card>

              <Card sx={{ borderRadius: 3, boxShadow: 'none', border: '1px solid #e1e5ea' }}>
                <CardContent>
                  <Typography sx={{ fontWeight: 800, fontSize: 17, color: MARINHO, mb: 1 }}>
                    Logo do município
                  </Typography>
                  <Box sx={{
                    height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    bgcolor: '#f5f7fa', border: '1px dashed #c5ccd6', borderRadius: 2, mb: 1.5,
                  }}>
                    {dados.logo_disponivel ? (
                      <img src={`${API_URL}/api/comunicacao/logo?v=${logoVer}`} alt="Logo do município"
                           style={{ maxWidth: '90%', maxHeight: 104, objectFit: 'contain' }} />
                    ) : (
                      <Typography sx={{ fontSize: 14, color: CINZA_TEXTO }}>Nenhuma logo enviada ainda</Typography>
                    )}
                  </Box>
                  <input ref={inputLogo} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={enviarLogo} />
                  <Button variant="outlined" startIcon={enviandoLogo ? <CircularProgress size={18} /> : <Upload />}
                          onClick={escolherLogo} disabled={enviandoLogo}
                          sx={{ minHeight: 44, borderColor: TEAL, color: TEAL }}>
                    {dados.logo_disponivel ? 'Trocar logo' : 'Enviar logo'}
                  </Button>
                  {erroLogo && <Alert severity="error" sx={{ mt: 1.5, py: 0 }}>{erroLogo}</Alert>}
                </CardContent>
              </Card>

              <Card sx={{ borderRadius: 3, boxShadow: 'none', border: '1px solid #e1e5ea' }}>
                <CardContent>
                  <Typography sx={{ fontWeight: 800, fontSize: 17, color: MARINHO, mb: 1 }}>
                    Como funciona
                  </Typography>
                  {[
                    'Os números nascem dos boletins (FAD-01 normais e de PE) e do Laboratório. Nada é digitado à mão.',
                    'Editar muda só este relatório. O boletim não é tocado. O cartão editado fica com borda azul e mostra o valor do sistema.',
                    'Copiar ou Exportar PDF e enviar à Coordenação até o dia 30.',
                  ].map((t, k) => (
                    <Box key={k} sx={{ display: 'flex', gap: 1.25, mb: 1.25 }}>
                      <Box sx={{
                        minWidth: 26, height: 26, borderRadius: '50%', bgcolor: TEAL, color: '#fff',
                        fontWeight: 800, fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>{k + 1}</Box>
                      <Typography sx={{ fontSize: 14, color: '#1b2430' }}>{t}</Typography>
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Box>
          )}
        </Box>
      </Box>

      {dados && printRoot && createPortal(<FolhaPDF dados={dados} logoVer={logoVer} />, printRoot)}

      {/* Aviso: exportar sem logo */}
      <Dialog open={dlgSemLogo} onClose={() => setDlgSemLogo(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Exportar sem logo?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Nenhuma logo enviada. O PDF sairá sem a logo do município. Exportar mesmo assim?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1, flexWrap: 'wrap' }}>
          <Button variant="outlined" onClick={() => { setDlgSemLogo(false); escolherLogo(); }} sx={{ minHeight: 44 }}>
            Enviar logo
          </Button>
          <Button variant="contained" onClick={() => { setDlgSemLogo(false); setTimeout(imprimir, 250); }}
                  sx={{ minHeight: 44, bgcolor: MARINHO }}>
            Exportar assim
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!aviso} autoHideDuration={3000} onClose={() => setAviso('')} message={aviso}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Box>
  );
}
